import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
  signInAnonymously,
  linkWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth, googleProvider, signInWithPopup } from './firebase';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (email, password, displayName = null) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update display name if provided
    if (displayName && userCredential.user) {
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
    }

    // Create user document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email,
      displayName: displayName || null,
      photoURL: null,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      circleIds: [],
      isAnonymous: false,
    });

    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing up:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Update last login
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      lastLoginAt: serverTimestamp(),
    }, { merge: true });

    return {
      user: userCredential.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in with Google (popup method)
 */
export const signInWithGooglePopup = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    
    // Update/create user document
    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: result.user.displayName || null,
      photoURL: result.user.photoURL || null,
      lastLoginAt: serverTimestamp(),
      isAnonymous: false,
    }, { merge: true });

    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Send password reset email
 */
export const resetPasswordEmail = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      error: null
    };
  } catch (error) {
    console.error('Error sending password reset:', error);
    return {
      error: error.message
    };
  }
};

/**
 * Send sign-in link to email (passwordless)
 */
export const sendEmailSignInLink = async (email) => {
  // Use the Art Ping Pong URL, not the current origin
  const hostname = window.location.hostname;
  let artPingPongUrl;
  
  if (hostname === 'localhost') {
    artPingPongUrl = 'http://localhost:3000';
  } else if (hostname.includes('artpingpong')) {
    artPingPongUrl = window.location.origin;
  } else {
    artPingPongUrl = 'https://artpingpong.com';
  }
    
  const actionCodeSettings = {
    url: artPingPongUrl + '/finishSignIn',
    handleCodeInApp: true,
  };

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    window.localStorage.setItem('emailForSignIn', email);
    return {
      error: null
    };
  } catch (error) {
    console.error('Error sending email link:', error);
    return {
      error: error.message
    };
  }
};

/**
 * Complete sign-in with email link
 */
export const completeEmailSignIn = async (emailLink) => {
  if (!isSignInWithEmailLink(auth, emailLink)) {
    return {
      user: null,
      error: 'Invalid sign-in link'
    };
  }

  let email = window.localStorage.getItem('emailForSignIn');
  if (!email) {
    email = window.prompt('Please provide your email for confirmation');
  }

  try {
    const result = await signInWithEmailLink(auth, email, emailLink);
    window.localStorage.removeItem('emailForSignIn');

    await setDoc(doc(db, 'users', result.user.uid), {
      email: result.user.email,
      displayName: result.user.displayName || null,
      photoURL: result.user.photoURL || null,
      lastLoginAt: serverTimestamp(),
      isAnonymous: false,
    }, { merge: true });

    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error completing email link sign-in:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Sign in anonymously
 */
export const signInAsAnonymous = async () => {
  try {
    const result = await signInAnonymously(auth);
    const userRef = doc(db, 'users', result.user.uid);
    await setDoc(userRef, {
      isAnonymous: true,
      displayName: 'Guest Artist',
      circleIds: [],
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }, { merge: true });
    window.localStorage.setItem('anonymousUid', result.user.uid);
    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error signing in anonymously:', error);
    return {
      user: null,
      error: error.message
    };
  }
};

/**
 * Link anonymous account to Google account
 */
export const linkAnonymousToGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await linkWithPopup(auth.currentUser, provider);
    const userRef = doc(db, 'users', result.user.uid);
    await updateDoc(userRef, {
      isAnonymous: false,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
      lastLoginAt: serverTimestamp(),
    });
    window.localStorage.removeItem('anonymousUid');
    return {
      user: result.user,
      error: null
    };
  } catch (error) {
    console.error('Error linking anonymous account:', error);
    
    // Handle case where Google account is already used (e.g., in DoMoreWorld)
    if (error.code === 'auth/credential-already-in-use') {
      return {
        user: null,
        error: 'This Google account is already in use. Please sign in with your existing account instead.',
        errorCode: 'credential-already-in-use'
      };
    }
    
    return {
      user: null,
      error: error.message
    };
  }
};
